import { useCallback, useEffect, useState } from "react";
import { useSelector } from "./hooks";
import {
  ClaimAccountState,
  ProgramState,
} from "../solana/redemption/redemptionClient";
import { WalletAdapterName } from "../adapters/WalletAdapter";
import { Wallet } from "@coral-xyz/anchor";
import { useDispatch } from "react-redux";
import { createNotificationAction } from "../actions/notification";
import TransactionLink from "../components/common/transaction-link/TransactionLink";
import logger from "../utils/logger";
import { getDisplayMessageFromError } from "../utils/error";

function useRedemption(): {
  loading: boolean;
  redeemUXPForUCT: (uxpAmount: number) => Promise<void>;
  burnUCTForUSDC: (uctAmount: number) => Promise<void>;
  calculateClaimableUSDCAmountPhaseOne: (uctAmount: number) => number;
  calculateMaxClaimableUSDCAmountPhaseTwo: () => number;
  redemptionState?: ProgramState;
  userState?: ClaimAccountState;
  claimIlliquidUSDC: (usdcAmountUi: number) => Promise<void>;
} {
  const loading = useSelector((state) => state.loading);
  const wallet = useSelector((state) => state.wallet);

  const [redemptionState, setRedemptionState] = useState<
    ProgramState | undefined
  >(undefined);
  const [userState, setUserState] = useState<ClaimAccountState | undefined>(
    undefined
  );
  const dispatch = useDispatch();
  const redemption = window.__UXD__.solana.services.redemption;
  const walletAdapter = window.__UXD__.solana.config.getWalletAdapter(
    wallet as WalletAdapterName
  );

  const calculateClaimableUSDCAmountPhaseOne = (
    uctAmountUi: number
  ): number => {
    if (!redemptionState || uctAmountUi === 0) {
      return 0;
    }
    const phaseOneTotalUsdcRedeemableAmount =
      redemptionState.phaseOneTotalUsdcRedeemableAmount;
    const uxpInitialSupply = redemptionState.uxpInitialSupply;
    const { decimals: uctDecimals } =
      window.__UXD__.solana.config.getMintInfo("UCT");
    const { decimals: usdcDecimals } =
      window.__UXD__.solana.config.getMintInfo("USDC");
    const userRedeemableNativeAmount =
      (phaseOneTotalUsdcRedeemableAmount * (uctAmountUi * 10 ** uctDecimals)) /
      uxpInitialSupply;
    return userRedeemableNativeAmount / 10 ** usdcDecimals;
  };
  const calculateMaxClaimableUSDCAmountPhaseTwo = useCallback((): number => {
    console.log("redemptionState", redemptionState);
    console.log("userState", userState);
    if (!redemptionState || !userState) {
      return 0;
    }

    const phaseTwoTotalUsdcRedeemableAmount =
      redemptionState.phaseTwoTotalRedeemableUsdcAmount;
    const redeemedUctAmount = redemptionState.redeemedUctAmount;
    const userBurntUctAmount = userState.burntUctAmount;
    const userPhaseTwoRedeemedUsdcAmount = userState.phaseTwoRedeemedUsdcAmount;
    const { decimals: usdcDecimals } =
      window.__UXD__.solana.config.getMintInfo("USDC");
    const amount =
      (phaseTwoTotalUsdcRedeemableAmount * userBurntUctAmount) /
        redeemedUctAmount -
      userPhaseTwoRedeemedUsdcAmount;
    return amount / 10 ** usdcDecimals;
  }, [redemptionState, userState]);

  useEffect(() => {
    const state = redemption.getState();
    setRedemptionState(state);

    const refresh = setInterval(async () => {
      const state = await redemption.refreshState();
      setRedemptionState(state);
    }, 10000);

    return () => {
      clearInterval(refresh);
    };
  }, [redemption]);

  useEffect(() => {
    if (!walletAdapter?.adapter?.publicKey) {
      return;
    }
    (async () => {
      await redemption.setProgramWithWallet(
        walletAdapter.adapter as any as Wallet
      );
      const uState = await redemption.refreshUserState(walletAdapter);
      setUserState(uState);
    })();
  }, [walletAdapter?.adapter?.publicKey]);

  const redeemUXPForUCT = async (uxpAmount: number): Promise<void> => {
    if (!walletAdapter.adapter?.publicKey) {
      throw new Error("Wallet is not connected.");
    }
    console.log("redeeming UXP for UCT");

    try {
      const txId = await redemption.redeemUXPForUCT(uxpAmount, walletAdapter);
      dispatch(
        createNotificationAction({
          title: "Redeem Successful",
          message: TransactionLink({
            message: "Your redeem transaction was a success",
            transaction: txId as unknown as string,
            cluster: window.__UXD__.solana.config.getClusterName(),
          }),
          level: "success",
          icon: "info",
        })
      );
    } catch (err) {
      logger.error(err);

      dispatch(
        createNotificationAction({
          title: "Redeem Error",
          message: "Transaction Failed",
          details: getDisplayMessageFromError(err),
          level: "error",
          icon: "info",
        })
      );
    } finally {
      const state = await redemption.refreshState();
      setRedemptionState(state);
      const userUpdatedState = await redemption.refreshUserState(walletAdapter);
      setUserState(userUpdatedState);
    }
  };

  const burnUCTForUSDC = async (uctAmount: number): Promise<void> => {
    if (!walletAdapter?.adapter?.publicKey) {
      throw new Error("Wallet is not connected.");
    }

    console.log("Burning UCT for USDC");

    try {
      const txId = await redemption.burnUCTForUSDC(uctAmount, walletAdapter);
      dispatch(
        createNotificationAction({
          title: "Burn Successful",
          message: TransactionLink({
            message: "Your burn transaction was a success",
            transaction: txId as unknown as string,
            cluster: window.__UXD__.solana.config.getClusterName(),
          }),
          level: "success",
          icon: "info",
        })
      );
    } catch (err) {
      logger.error(err);

      dispatch(
        createNotificationAction({
          title: "Burn Error",
          message: "Transaction Failed",
          details: getDisplayMessageFromError(err),
          level: "error",
          icon: "info",
        })
      );
    } finally {
      const state = await redemption.refreshState();
      setRedemptionState(state);
      const userUpdatedState = await redemption.refreshUserState(walletAdapter);
      setUserState(userUpdatedState);
    }
  };

  const claimIlliquidUSDC = async (usdcAmountUi: number): Promise<void> => {
    if (!walletAdapter?.adapter?.publicKey) {
      throw new Error("Wallet is not connected.");
    }
    if (
      !!redemptionState?.phaseOneEndTimestamp &&
      redemptionState?.phaseOneEndTimestamp * 1000 > Date.now()
    ) {
      throw new Error("Phase one is not ended yet.");
    }

    const maxClaimable = calculateMaxClaimableUSDCAmountPhaseTwo();
    if (usdcAmountUi > maxClaimable) {
      throw new Error("Claim amount is greater than max claimable amount.");
    }

    try {
      const txId = await redemption.claimIlliquidUSDC(
        usdcAmountUi,
        walletAdapter
      );
      dispatch(
        createNotificationAction({
          title: "Claim Successful",
          message: TransactionLink({
            message: "Your claim transaction was a success",
            transaction: txId as unknown as string,
            cluster: window.__UXD__.solana.config.getClusterName(),
          }),
          level: "success",
          icon: "info",
        })
      );
    } catch (err) {
      logger.error(err);

      dispatch(
        createNotificationAction({
          title: "Claim Error",
          message: "Transaction Failed",
          details: getDisplayMessageFromError(err),
          level: "error",
          icon: "info",
        })
      );
    }
  };

  return {
    loading,
    redeemUXPForUCT,
    burnUCTForUSDC,
    redemptionState,
    userState,
    calculateClaimableUSDCAmountPhaseOne,
    calculateMaxClaimableUSDCAmountPhaseTwo,
    claimIlliquidUSDC,
  };
}

export default useRedemption;
